/**
 * Fleet Mode Phase 1 — pure access-mode derivation.
 *
 * Resolves a charger's effective access classification given:
 *   - `charger.fleet_id` — null = public charger; set = fleet-attributed
 *   - `charger.allow_mixed_use` — three-state per-charger override
 *     (true / false / null = inherit)
 *   - `fleet.allows_mixed_use_default` — parent-fleet default consumed
 *     only when the per-charger field is null
 *
 * Returns:
 *   - 'public'     — no fleet attribution; any driver can charge
 *                    (subject to other gates: guest-access, tariffs, …).
 *   - 'fleet_only' — fleet attribution AND mixed-use is OFF; only
 *                    drivers with matching `fleet_id` AND
 *                    `current_mode === 'fleet'` may start sessions.
 *   - 'mixed'      — fleet attribution AND mixed-use is ON; fleet
 *                    drivers in 'fleet' mode bill against the fleet
 *                    customer; non-fleet drivers (or fleet drivers
 *                    in 'public' mode) bill personally.
 *
 * Pure / side-effect-free. Accepts plain-object shapes (not Mongoose
 * documents) so it can run on tRPC payloads, BFF maps, and tests
 * without an ODM dependency. Phase 1 ships the helper but no read or
 * write path is bound to it yet — Phase 2 wires the API gate on top.
 *
 * `fleet` may be `null` even when `charger.fleet_id` is set (e.g. the
 * caller fetched the charger but skipped the Fleet join). In that
 * case the helper falls back to `false` for mixed-use, returning
 * 'fleet_only' — the conservative default. Callers that need the
 * authoritative answer for a mixed-use-default-true fleet must
 * populate the fleet argument.
 */
export function deriveChargerAccessMode(charger, fleet) {
    if (!charger.fleet_id)
        return 'public';
    const mixed = charger.allow_mixed_use ?? fleet?.allows_mixed_use_default ?? false;
    return mixed ? 'mixed' : 'fleet_only';
}
//# sourceMappingURL=chargerAccessMode.js.map